/**
 * SPEC-TrainingSimulator §8.3 / D18 — the Phase 2 review screen.
 *
 * Phase 2 records silently: nothing about the key is shown while the trainee
 * works. Everything they earned arrives here at once — per item, per question,
 * correct or wrong, with the rationale and the keyed answer.
 *
 * §12 Q6, settled by Sarah 2026-09-09: this screen DOES show an aggregate —
 * a grand total plus a per-item count. D7's "the trainee never sees a score"
 * covers the graded phases 3–4; Phase 2 is the practice phase, and a trainee
 * who cannot tell 55/60 from 20/60 cannot tell whether they are ready.
 *
 * A question the key does not ask (Q8 on a "returns collected" delivery) is
 * listed as "not asked" and counted in neither total, so an item is never
 * marked down for a question that was never really put.
 *
 * "Continue to Phase 3" dismisses (§5 gate) and returns to /train.
 */

import { useState } from "react";
import { Starling } from "@/lib/starling-brand";
import { QUESTION_SHORT_LABEL, type EvidenceOption } from "@/components/simulator/question-controls";
import { describeEntry } from "@/components/simulator/question-rail";
import type { ReviewItem, ReviewQuestion, RunnerReview } from "@/lib/simulator-runner-queries";
import { notAskedReason, type SimulatorQuestionConfig } from "@shared/simulator-helpers";

const T = {
  card: "#FFFFFF",
  bg: "#F7F8FA",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.08)",
  purpleSubB: "rgba(89,47,150,.25)",
  good: "#166534",
  goodBg: "#DCFCE7",
  goodBorder: "#86EFAC",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  badBorder: "#FECACA",
} as const;

export interface ReviewScreenProps {
  review: RunnerReview;
  /** The phase's question config, for the human descriptions of an answer. */
  questions: SimulatorQuestionConfig[];
  evidence: (item: ReviewItem) => EvidenceOption[];
  onContinue: () => void;
}

const glyph = (status: ReviewQuestion["status"]) => (status === "correct" ? "✓" : status === "not_asked" ? "–" : "✗");
const tone = (status: ReviewQuestion["status"]) =>
  status === "correct"
    ? { fg: T.good, bg: T.goodBg, bd: T.goodBorder }
    : status === "not_asked"
      ? { fg: T.txt2, bg: T.bg, bd: T.border }
      : { fg: T.bad, bg: T.badBg, bd: T.badBorder };

export function ReviewScreen({ review, questions, evidence, onContinue }: ReviewScreenProps) {
  const [open, setOpen] = useState<string | null>(review.items[0]?.itemId ?? null);
  const { correct, graded, perItem } = review.totals;

  return (
    <div data-testid="train-review" style={{ padding: "12px 18px 40px", fontFamily: Starling.fontBody, color: T.text, background: T.bg, minHeight: "100%" }}>
      {/* Headline — the one aggregate a trainee ever sees (§12 Q6). */}
      <div
        style={{
          background: T.card, border: `1px solid ${T.border}`, borderLeft: `4px solid ${T.purple}`, borderRadius: 8,
          padding: "16px 18px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 18 }}>Phase 2 complete — how you did</div>
          <div style={{ fontSize: 13, color: T.txt2, marginTop: 4 }}>
            Every question you answered, with the keyed answer and why. Open a delivery to read it back.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div data-testid="review-total" style={{ textAlign: "right" }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: T.purple }}>{correct} of {graded}</div>
            <div style={{ fontSize: 11.5, color: T.txt3 }}>questions correct</div>
          </div>
          <button
            type="button"
            data-testid="review-continue"
            onClick={onContinue}
            style={{
              padding: "11px 20px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: Starling.fontBody,
              border: `1px solid ${T.purple}`, background: T.purple, color: "#fff", cursor: "pointer",
            }}
          >
            Continue to Phase 3 ›
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {review.items.map((item, i) => {
          const counts = perItem[i] ?? { correct: 0, graded: 0 };
          const isOpen = open === item.itemId;
          return (
            <div key={item.itemId} data-testid={`review-item-${item.index}`} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
              <button
                type="button"
                data-testid={`review-item-toggle-${item.index}`}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : item.itemId)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
                  background: isOpen ? T.purpleSub : T.card, border: "none", borderBottom: isOpen ? `1px solid ${T.border}` : "none",
                  cursor: "pointer", fontFamily: Starling.fontBody, textAlign: "left", color: T.text,
                }}
              >
                <span style={{ fontSize: 12, color: T.txt3, width: 22 }}>{isOpen ? "▾" : "▸"}</span>
                <span style={{ fontWeight: 700, fontSize: 13.5 }}>Delivery {item.index + 1}</span>
                <span style={{ fontSize: 12.5, color: T.txt2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {item.recipientName ?? "—"}{item.address ? ` · ${item.address}` : ""}
                </span>
                {/* Per-item count (§12 Q6) — the chips beside it say which ones. */}
                <span
                  data-testid={`review-item-count-${item.index}`}
                  style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: T.txt2, whiteSpace: "nowrap" }}
                >
                  {counts.correct} of {counts.graded}
                </span>
                <span style={{ display: "inline-flex", gap: 3 }}>
                  {item.questions.map((q) => {
                    const c = tone(q.status);
                    return (
                      <span
                        key={q.questionId}
                        aria-label={`Q${q.sort} · ${QUESTION_SHORT_LABEL[q.questionId]} · ${q.status.replace("_", " ")}`}
                        title={`Q${q.sort} · ${QUESTION_SHORT_LABEL[q.questionId]}`}
                        style={{
                          width: 20, height: 20, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: c.fg, background: c.bg,
                          border: `1px ${q.status === "not_asked" ? "dashed" : "solid"} ${c.bd}`,
                        }}
                      >
                        {glyph(q.status)}
                      </span>
                    );
                  })}
                </span>
              </button>
              {isOpen && (
                <div style={{ padding: "10px 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {item.questions.map((q) => (
                    <ReviewRow key={q.questionId} q={q} config={questions.find((c) => c.id === q.questionId)} evidence={evidence(item)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button
          type="button"
          data-testid="review-continue-bottom"
          onClick={onContinue}
          style={{
            padding: "11px 20px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: Starling.fontBody,
            border: `1px solid ${T.purple}`, background: T.purple, color: "#fff", cursor: "pointer",
          }}
        >
          Continue to Phase 3 ›
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ q, config, evidence }: { q: ReviewQuestion; config: SimulatorQuestionConfig | undefined; evidence: EvidenceOption[] }) {
  const c = tone(q.status);
  return (
    <div
      data-testid={`review-question-${q.questionId}`}
      data-status={q.status}
      style={{ border: `1px ${q.status === "not_asked" ? "dashed" : "solid"} ${c.bd}`, background: c.bg, borderRadius: 8, padding: "9px 11px" }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontWeight: 700, color: c.fg, fontSize: 13 }}>{glyph(q.status)}</span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          <span style={{ color: T.purple, marginRight: 6 }}>Q{q.sort}</span>{config?.text.prompt ?? q.prompt}
        </span>
      </div>
      {q.status === "not_asked" ? (
        <div style={{ fontSize: 12.5, color: T.txt2, marginTop: 5 }}>
          Not asked for this delivery — {notAskedReason(q.questionId)}. It is not counted either way.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 5, fontSize: 12.5 }}>
          <div style={{ color: T.txt2 }}>
            <span style={{ fontWeight: 600, color: T.text }}>You answered:</span>{" "}
            {config ? describeEntry(q.questionId, q.answer, config, evidence) : "—"}
          </div>
          {q.status !== "correct" && config && (
            <div style={{ color: T.txt2 }}>
              <span style={{ fontWeight: 600, color: T.text }}>Keyed answer:</span>{" "}
              {describeEntry(q.questionId, q.reveal?.keyed ?? null, config, evidence)}
            </div>
          )}
          {q.feedback?.text && <div data-testid="review-question-feedback" style={{ lineHeight: 1.45 }}>{q.feedback.text}</div>}
        </div>
      )}
    </div>
  );
}

export default ReviewScreen;
